import { useMemo, useState } from 'react';

import {
  faHouseMedical,
  faLocationDot,
  faMagnifyingGlass,
  faUser,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataTable } from '@repo/videa-components/src/index';
import { Input } from '@repo/videa-components/src/ui/input';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type { TInsightsTableData } from '../../components/BenchmarkingTableColumns';
import { useGetBenchmarkingTableColumns } from '../../components/BenchmarkingTableColumns';
import { TableNavItem } from '../../components/TableNavItem';

export const AIBenchmarksTable = ({
  providerRates,
  practiceRates,
  regionRates,
  isLoading,
}: {
  providerRates: TInsightsTableData[];
  practiceRates: TInsightsTableData[];
  regionRates: TInsightsTableData[];
  isLoading?: boolean;
}) => {
  const [selectedDashboard, setSelectedDashboard] = useState<
    'Provider' | 'Practice' | 'Region'
  >('Provider');

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns: ColumnDef<TInsightsTableData, unknown>[] =
    useGetBenchmarkingTableColumns({
      drilldownLevel: selectedDashboard,
    });

  const data = useMemo(() => {
    if (selectedDashboard === 'Provider') {
      return providerRates;
    }
    if (selectedDashboard === 'Practice') {
      return practiceRates;
    }
    return regionRates;
  }, [selectedDashboard, providerRates, practiceRates, regionRates]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    enableRowSelection: true,
    state: {
      sorting,
      columnFilters,
    },
  });

  return (
    <div className="vai-flex vai-flex-col vai-gap-4">
      <div className="vai-flex vai-flex-row vai-justify-between">
        <div className="vai-flex vai-flex-row vai-gap-2">
          <TableNavItem
            dataTestId="dso-overview-nav-item"
            onClick={() => setSelectedDashboard('Region')}
            selected={selectedDashboard === 'Region'}
          >
            <FontAwesomeIcon icon={faLocationDot} />
            Region
          </TableNavItem>
          <TableNavItem
            dataTestId="practice-nav-item"
            onClick={() => setSelectedDashboard('Practice')}
            selected={selectedDashboard === 'Practice'}
          >
            <FontAwesomeIcon icon={faHouseMedical} />
            Practice
          </TableNavItem>
          <TableNavItem
            dataTestId="provider-nav-item"
            onClick={() => setSelectedDashboard('Provider')}
            selected={selectedDashboard === 'Provider'}
          >
            <span className="vai-flex vai-items-center vai-gap-2">
              <FontAwesomeIcon icon={faUser} />
              Providers
            </span>
          </TableNavItem>
        </div>
        <div className="vai-flex vai-flex-row vai-gap-4">
          <div className="vai-relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-z-10"
            />
            <Input
              placeholder={`Search ${selectedDashboard}s`}
              value={
                (table.getColumn('name')?.getFilterValue() as string) ?? ''
              }
              onChange={(event) =>
                table.getColumn('name')?.setFilterValue(event.target.value)
              }
              className="vai-w-full vai-pl-10 vai-pr-8"
            />
          </div>
        </div>
      </div>
      <DataTable
        table={table}
        className="vai-h-[calc(100vh-400px)]"
        isLoading={isLoading}
      />
    </div>
  );
};
